
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { X, Cookie } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

export const CookieConsent = () => {
    const { t } = useTranslation();
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        // Check if user has already made a choice
        const hasConsented = localStorage.getItem("cookieConsent");
        if (!hasConsented) {
            // Small delay for better UX
            const timer = setTimeout(() => setIsVisible(true), 1500);
            return () => clearTimeout(timer);
        }
    }, []);

    const handleAccept = () => {
        localStorage.setItem("cookieConsent", "accepted");
        setIsVisible(false);
    };

    const handleDecline = () => {
        localStorage.setItem("cookieConsent", "declined");
        setIsVisible(false);
    };

    return (
        <AnimatePresence>
            {isVisible && (
                // ElevenLabs uses z-index 1000; center consent on mobile above the widget.
                <div className="pointer-events-none fixed inset-0 z-[1001] flex items-center justify-center p-4 md:inset-auto md:bottom-8 md:right-8 md:w-full md:max-w-sm md:p-0">
                    <motion.div
                        initial={{ y: 100, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 100, opacity: 0 }}
                        transition={{ type: "spring", stiffness: 300, damping: 30 }}
                        className="pointer-events-auto max-h-[calc(100dvh-2rem)] w-full max-w-sm overflow-y-auto p-5 sm:p-6 bg-background/95 backdrop-blur-xl border border-primary/20 rounded-2xl shadow-2xl shadow-primary/10"
                    >
                        <div className="flex items-start gap-3 sm:gap-4">
                            <div className="p-2 sm:p-3 bg-primary/10 rounded-xl text-primary shrink-0">
                                <Cookie className="w-6 h-6" />
                            </div>
                            <div className="min-w-0 flex-1 space-y-3">
                                <h3 className="font-semibold text-lg leading-tight">{t('cookie_banner.title')}</h3>
                                <p className="text-sm text-muted-foreground leading-relaxed">
                                    {t('cookie_banner.text')}
                                    <Link to="/cookies" className="text-primary hover:underline ml-1">
                                        {t('cookie_banner.link')}
                                    </Link>
                                </p>
                            </div>
                            <button
                                onClick={() => setIsVisible(false)}
                                className="shrink-0 text-muted-foreground hover:text-foreground transition-colors"
                                aria-label={t('cookie_banner.close_button') || "Cerrar aviso de cookies"}
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="grid grid-cols-2 gap-3 mt-6">
                            <Button
                                variant="outline"
                                onClick={handleDecline}
                                className="w-full bg-transparent border-border hover:bg-secondary/50"
                            >
                                {t('cookie_banner.decline')}
                            </Button>
                            <Button
                                variant="outline"
                                onClick={handleAccept}
                                className="w-full bg-transparent border-border hover:bg-secondary/50"
                            >
                                {t('cookie_banner.accept')}
                            </Button>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};
